import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import '@semsis/ui/base.css';
import { initSentry } from '@referee/error-handling';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { createApolloClient } from '@referee/graphql-client';
import './i18n';
import { App } from './App';
import { CredentialError } from './components/CredentialError';

initSentry();

// NOTE: Eagerly awaiting credentialsProvider() before createRoot is INTENTIONAL, mirroring
// the sibling anonymous test frontend's established precedent. An anonymous visitor with no
// working IAM credentials cannot load any catalog, so rendering the shell first -- only to
// have every query fail -- would be misleading. The eager await validates IAM connectivity
// up front and falls back to a friendly retry screen if it fails, instead of a silently
// broken shell.
async function bootstrap() {
  const region = import.meta.env.VITE_AWS_REGION ?? 'eu-central-1';
  const identityPoolId = import.meta.env.VITE_IDENTITY_POOL_ID;

  if (!identityPoolId) {
    throw new Error('VITE_IDENTITY_POOL_ID is not configured');
  }

  // Create lazy credential provider (no network call yet)
  const credentialsProvider = fromCognitoIdentityPool({
    identityPoolId,
    clientConfig: { region },
  });

  // Eagerly validate credentials work before mounting the app.
  // Triggers the first GetId + GetCredentialsForIdentity call.
  await credentialsProvider();

  // Create Apollo client with the refreshable provider (not resolved creds).
  // The provider is called per-request by aws-appsync-auth-link for SigV4 signing.
  const client = createApolloClient({
    graphqlEndpoint: '/graphql',
    appSyncHost: import.meta.env.VITE_APPSYNC_HOST,
    region,
    credentials: credentialsProvider,
  });

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App apolloClient={client} />
    </StrictMode>,
  );
}

function renderError() {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <CredentialError onRetry={() => { bootstrap().catch(handleError); }} />
    </StrictMode>,
  );
}

function handleError(err: unknown) {
  Sentry.captureException(err);
  console.error('Failed to bootstrap training app:', err);
  renderError();
}

bootstrap().catch(handleError);
